import { useState } from 'react';
import { Icon } from '@iconify/react';
import { MessageSquare, Brain, Shield, Users, Play, Code, Zap } from 'lucide-react';
import IntegrationLandingBase from '@/components/integrations/IntegrationLandingBase';

export default function SlackLanding() {
  const [copiedCode, setCopiedCode] = useState(false);

  const handleCopySetupCode = async () => {
    const setupCode = `# 1. Install Memara Bot in Slack\n/app install memara\n\n# 2. Connect to your workspace\n@memara setup workspace\n\n# 3. Start capturing memories\n@memara remember this important discussion\n\n# 4. Search team knowledge\n@memara search "Q3 strategy"`;
    try {
      await navigator.clipboard.writeText(setupCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (error) {
      const textArea = document.createElement('textarea');
      textArea.value = setupCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleGetStarted = () => {
    window.location.href = '/login';
  };
  const handleSeeDemo = () => {
    window.open('https://www.youtube.com/watch?v=ouW739Hx7v8', '_blank');
  };

  return (
    <IntegrationLandingBase
      registryIntegrationId="slack_bot"
      icon={
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e8f5fd] shadow" aria-label="Slack Logo">
          <Icon icon="logos:slack-icon" width={48} height={48} />
        </span>
      }
      title="Slack Integration"
      description="Enterprise team memory for Slack workspaces. Transform workplace conversations into searchable knowledge with intelligent extraction, semantic understanding, and persistent context retention for distributed development teams."
      features={[
        { icon: <MessageSquare className="h-4 w-4 text-[#611f69]" />, title: 'Conversation Intelligence', description: 'Extract and contextualize key information from team discussions with AI-powered content analysis.' },
        { icon: <Brain className="h-4 w-4 text-[#611f69]" />, title: 'Semantic Workplace Search', description: 'Advanced search capabilities across all team communications with natural language understanding.' },
        { icon: <Shield className="h-4 w-4 text-[#611f69]" />, title: 'Workspace Isolation', description: 'Multi-tenant workspace isolation with access controls for each Slack workspace.' },
        { icon: <Users className="h-4 w-4 text-[#611f69]" />, title: 'Team Knowledge Management', description: 'Shared organizational memory with role-based access controls and audit trail capabilities.' },
        { icon: <Code className="h-4 w-4 text-[#611f69]" />, title: 'Developer-Friendly APIs', description: 'RESTful memory operations with webhook support for custom integrations and workflow automation.' },
        { icon: <Zap className="h-4 w-4 text-[#611f69]" />, title: 'Real-time Processing', description: 'Instant memory creation and search with sub-200ms response times for seamless team workflows.' },
      ]}
      setupSteps={[
        'Install Memara Bot in your Slack workspace',
        'Connect and configure workspace permissions',
        'Start capturing team knowledge and discussions',
        'Search and access organizational memory instantly',
      ]}
      ctas={[
        {
          label: '🚀 Get Started Free',
          onClick: handleGetStarted,
          className: 'bg-[#611f69] hover:bg-[#4a154b] text-white px-8 py-4 h-11 rounded-md text-base font-semibold',
        },
        {
          label: 'See Demo',
          onClick: handleSeeDemo,
          className: 'border-[#611f69] text-[#611f69] hover:bg-[#f4e7f8] px-8 py-4 h-11 rounded-md text-base border',
          icon: <Play className="w-4 h-4 mr-2" />,
        },
        {
          label: copiedCode ? 'Copied!' : 'Copy Setup Code',
          onClick: handleCopySetupCode,
          className: 'border-[#611f69] text-[#611f69] hover:bg-[#f4e7f8] px-8 py-4 h-11 rounded-md text-base border',
          icon: copiedCode ? <Brain className="w-4 h-4 mr-2" /> : <Icon icon="logos:slack-icon" width={20} height={20} />, 
        },
      ]}
      screenshots={[
        {
          id: 'slack-bot-install',
          title: 'Install Memara Bot',
          description: 'Add Memara to your Slack workspace with enterprise-grade security permissions',
          placeholder: 'Slack app installation dialog showing Memara bot with workspace permissions',
          badge: 'Setup'
        },
        {
          id: 'team-memory-capture',
          title: 'Team Memory Capture',
          description: 'Capture important discussions and decisions with @memara commands in any channel',
          placeholder: 'Slack conversation showing @memara bot creating memories from team discussions',
          badge: 'In Action'
        },
        {
          id: 'knowledge-search',
          title: 'Workplace Knowledge Search',
          description: 'Search all team knowledge with intelligent semantic understanding and context',
          placeholder: 'Slack interface showing Memara search results across team conversations',
          badge: 'Search'
        }
      ]}
      videoUrl="https://www.youtube.com/watch?v=ouW739Hx7v8"
      microcopy="Trusted by engineering teams and enterprise organizations using Slack for distributed team collaboration and knowledge management."
      trustedBy="Slack, Enterprise Development Teams, and more."
      testimonial="Memara's Slack integration transforms our team communications into a searchable knowledge base that actually understands context and technical discussions."
    />
  );
} 